/*! compact-product-bundle — Session 1: coverage + skip + live total */
(function () {
  if (customElements.get('compact-product-bundle')) return;

  // Round up to nearest 0.5 (safe purchase qty — matches calculator)
  function roundUpHalf(n) {
    return Math.ceil(n * 2) / 2;
  }

  // Format pence → £X.XX
  function formatMoney(pence) {
    return '£' + (pence / 100).toFixed(2);
  }

  /**
   * Tin-picking algorithm — price-optimised.
   * Same logic as the coverage calculator. Enumerates combos,
   * scores by total price, returns the cheapest that covers `needed`.
   */
  function pickTins(available, needed) {
    const sorted = [...available].sort((a, b) => b.volume - a.volume);
    const maxQtys = sorted.map((v) => Math.ceil(needed / v.volume));
    const combinations = [];

    function recurse(index, current, volSoFar, priceSoFar) {
      if (volSoFar >= needed) {
        const pruned = current.filter((p) => p.qty > 0);
        const reallyNeeded = pruned.every(
          (p) => volSoFar - p.variant.volume < needed - 0.001
        );
        if (reallyNeeded) {
          combinations.push({
            picks: pruned.map((p) => ({ ...p })),
            totalVolume: volSoFar,
            totalPrice: priceSoFar,
          });
        }
        return;
      }
      if (index >= sorted.length) return;

      const v = sorted[index];
      const maxQty = maxQtys[index];
      for (let q = 0; q <= maxQty; q++) {
        const next = q === 0 ? current : [...current, { variant: v, qty: q }];
        recurse(index + 1, next, volSoFar + q * v.volume, priceSoFar + q * v.price);
      }
    }

    recurse(0, [], 0, 0);
    if (combinations.length === 0) return null;

    // Cheapest wins, tin-count is tiebreaker
    combinations.sort((a, b) => {
      const aTins = a.picks.reduce((s, p) => s + p.qty, 0);
      const bTins = b.picks.reduce((s, p) => s + p.qty, 0);
      return (a.totalPrice + aTins * 0.01) - (b.totalPrice + bTins * 0.01);
    });
    return combinations[0];
  }


  class CompactProductBundle extends HTMLElement {
    constructor() {
      super();
      this.submitHandler = this.submit.bind(this);
      this.variantChangeHandler = this.handleVariantChange.bind(this);
      this.areaChangeHandler = this.recalculate.bind(this);
      this.skipChangeHandler = this.handleSkipChange.bind(this);
    }

    connectedCallback() {
      this.submitButton = this.querySelector('[data-product-bundle-submit]');
      this.submitButtonText = this.querySelector('[data-product-bundle-submit-text]');
      this.errorMessage = this.querySelector('.product-form__error-message');
      this.areaInput = this.querySelector('[data-bundle-area]');
      this.coatsInput = this.querySelector('[data-bundle-coats]');
      this.totalEl = this.querySelector('[data-bundle-total]');
      this.totalValueEl = this.querySelector('[data-bundle-total-value]');
      this.calcHintEl = this.querySelector('[data-bundle-calc-hint]');

      this.coverageEnabled = this.dataset.enableCoverage === 'true';
      this.cards = Array.from(this.querySelectorAll('[data-bundle-card]'));

      // Parse variant data per card
      this.cards.forEach((card) => {
        const script = card.querySelector('[data-bundle-variants]');
        if (script) {
          try {
            card.__variants = JSON.parse(script.textContent);
          } catch (e) {
            card.__variants = [];
          }
        } else {
          card.__variants = [];
        }

        // Hide size dropdown if coverage is driving this card
        if (card.dataset.useCoverage === 'true') {
          this.hideSizeDropdown(card);
        }

        // Wire skip toggle
        const skip = card.querySelector('[data-bundle-card-skip]');
        if (skip) {
          skip.addEventListener('change', this.skipChangeHandler);
        }
      });

      // Wire submit + variant change
      if (this.submitButton) {
        this.submitButton.addEventListener('click', this.submitHandler);
      }
      this.addEventListener('on:variant:change', this.variantChangeHandler);

      // Wire area/coats inputs
      if (this.areaInput) {
        this.areaInput.addEventListener('input', this.debounce(this.areaChangeHandler, 200));
      }
      if (this.coatsInput) {
        this.coatsInput.addEventListener('input', this.debounce(this.areaChangeHandler, 200));
      }

      // Initial state
      this.updateSubmitButton();
    }

    disconnectedCallback() {
      if (this.submitButton) {
        this.submitButton.removeEventListener('click', this.submitHandler);
      }
      this.removeEventListener('on:variant:change', this.variantChangeHandler);
    }

    // ==============================================================
    // UI helpers
    // ==============================================================

    debounce(fn, delay) {
      let t;
      return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), delay);
      };
    }

    /**
     * Hide the Size dropdown inside a card (coverage mode auto-picks
     * the right size(s) so the user shouldn't touch it).
     *
     * Enterprise renders each variant option as:
     *   <div class="option-selector">
     *     <label class="label">Size</label>
     *     <custom-select>...</custom-select>
     *   </div>
     *
     * We find the option-selector whose label text matches "Size" and
     * hide the whole fieldset.
     */
    hideSizeDropdown(card) {
      const selectors = card.querySelectorAll('.option-selector');
      selectors.forEach((sel) => {
        const label = sel.querySelector('.label');
        if (!label) return;
        const text = label.textContent.trim().toLowerCase();
        if (text === 'size') {
          sel.style.display = 'none';
          sel.dataset.ccHiddenBySize = 'true';
        }
      });
    }

    // ==============================================================
    // Variant change (user picks colour/anti-slip)
    // ==============================================================

    handleVariantChange(event) {
      const variant = event.detail && event.detail.variant;
      if (!variant) return;
      const card = event.target.closest('[data-bundle-card]');
      if (!card) return;

      // Sync hidden input (used in non-coverage mode)
      const input = card.querySelector('[data-bundle-variant-id]');
      if (input) input.value = variant.id;

      // Update card price display
      this.updateCardPrice(card, event.target, variant);

      // If coverage is active, a colour/anti-slip change can mean
      // a different variant family — recalculate tin pick for this card
      if (this.coverageEnabled && card.dataset.useCoverage === 'true') {
        this.recalculate();
      } else {
        this.updateTotal();
      }
    }

    updateCardPrice(card, picker, variant) {
      const priceEl = card.querySelector('.price--card-bundle');
      if (!priceEl) return;
      let formatted = null;
      try {
        const data = JSON.parse(picker.querySelector('script[type="application/json"]').textContent);
        formatted = data.formatted && data.formatted[variant.id];
      } catch (e) { return; }
      if (!formatted) return;

      const currentEl = priceEl.querySelector('.price__current');
      if (currentEl && formatted.price) currentEl.innerHTML = formatted.price;

      const wasEl = priceEl.querySelector('.price__was');
      if (wasEl) wasEl.innerHTML = formatted.compareAtPrice || '';

      priceEl.classList.toggle('price--on-sale',
        !!(variant.compare_at_price && variant.compare_at_price > variant.price));
      priceEl.classList.toggle('price--sold-out', variant.available === false);

      const fromEl = priceEl.querySelector('.price__from');
      if (fromEl) fromEl.hidden = true;
    }

    // ==============================================================
    // Skip toggle
    // ==============================================================

    handleSkipChange(event) {
      const card = event.target.closest('[data-bundle-card]');
      if (!card) return;
      card.classList.toggle('is-skipped', event.target.checked);
      this.updateTotal();
    }

    // ==============================================================
    // Coverage recalculation — the core logic
    // ==============================================================

    recalculate() {
      if (!this.coverageEnabled) {
        this.updateTotal();
        return;
      }

      const area = parseFloat(this.areaInput && this.areaInput.value) || 0;

      // User's topcoat coats override — only topcoat cards use this.
      // If blank/invalid, we fall back to each card's own default_coats.
      const rawUserCoats = parseInt(this.coatsInput && this.coatsInput.value, 10);
      const userCoats = (rawUserCoats >= 1 && rawUserCoats <= 10) ? rawUserCoats : null;

      if (area <= 0) {
        // Area cleared — reset all cards to single-unit mode
        this.cards.forEach((card) => {
          card.__tinPick = null;
          this.hideCardRec(card);
        });
        if (this.totalEl) this.totalEl.hidden = true;
        if (this.calcHintEl) {
          this.calcHintEl.textContent = 'Enter your area to see recommended quantities.';
          this.calcHintEl.hidden = false;
        }
        this.updateSubmitButton();
        return;
      }

      // Hide the hint once we have a value
      if (this.calcHintEl) this.calcHintEl.hidden = true;

      // For each card with coverage, work out needed qty + tin pick
      this.cards.forEach((card) => {
        if (card.dataset.useCoverage !== 'true') return;

        const rate = parseFloat(card.dataset.coverageRate);
        const unit = card.dataset.coverageUnit || 'Litres';
        if (!rate || rate <= 0) return;

        // Work out coats for THIS card:
        //   - Topcoat cards (uses_user_coats=true): user's input, or own default if blank
        //   - Everything else (primer, prep): own default_coats
        const defaultCoats = parseInt(card.dataset.defaultCoats, 10) || 1;
        const usesUserCoats = card.dataset.usesUserCoats === 'true';
        const cardCoats = usesUserCoats
          ? (userCoats != null ? userCoats : defaultCoats)
          : defaultCoats;

        // Needed = area * coats / coverage_rate
        const needed = roundUpHalf((area * cardCoats) / rate);

        // Get the user's current non-size selection from Enterprise's
        // variant-picker (so if they chose Black, we pick Black tins)
        const selection = this.getCardSelection(card);

        // Filter variants by non-size selection + availability + has volume
        const sizeIdx = parseInt(card.dataset.sizeOptionIndex, 10);
        const matching = card.__variants.filter((v) => {
          if (!v.available) return false;
          if (v.volume == null || v.volume <= 0) return false;
          return Object.keys(selection).every((idx) => {
            return v.options[idx] === selection[idx];
          });
        });

        const pick = matching.length ? pickTins(matching, needed) : null;
        card.__tinPick = pick;
        card.__neededQty = needed;
        card.__coverageUnit = unit;
        card.__coats = cardCoats;

        this.renderCardRec(card, pick, needed, unit);
      });

      this.updateTotal();
      this.updateSubmitButton();
    }

    /**
     * Read the user's current non-size option selections from this card.
     * Returns { optionIndex: value } for every non-size option.
     * Reads from the currently-selected variant ID in the hidden form.
     */
    getCardSelection(card) {
      const sizeIdx = parseInt(card.dataset.sizeOptionIndex, 10);
      const input = card.querySelector('[data-bundle-variant-id]');
      const currentId = input ? parseInt(input.value, 10) : NaN;
      const current = card.__variants.find((v) => v.id === currentId)
        || card.__variants.find((v) => v.available);
      if (!current) return {};

      const selection = {};
      current.options.forEach((val, idx) => {
        if (idx !== sizeIdx) selection[idx] = val;
      });
      return selection;
    }

    renderCardRec(card, pick, needed, unit) {
      const rec = card.querySelector('[data-bundle-card-rec]');
      const summary = card.querySelector('[data-bundle-card-rec-summary]');
      if (!rec || !summary) return;

      if (!pick) {
        summary.innerHTML =
          '<span class="bundle-card-rec__warning">' +
          'No matching size available — please pick a different combination.' +
          '</span>';
        rec.hidden = false;
        return;
      }

      const tinStrings = pick.picks.map((p) => {
        const tinLabel = p.variant.options
          .filter((_, i) => i === parseInt(card.dataset.sizeOptionIndex, 10))
          .join(' ');
        return p.qty + ' × ' + (tinLabel || p.variant.title);
      });

      summary.innerHTML =
        '<span class="bundle-card-rec__need">You need ' + needed + ' ' + unit + '</span>' +
        '<span class="bundle-card-rec__pick">' + tinStrings.join(' + ') + '</span>' +
        '<span class="bundle-card-rec__price">' + formatMoney(pick.totalPrice) + '</span>';
      rec.hidden = false;
    }

    hideCardRec(card) {
      const rec = card.querySelector('[data-bundle-card-rec]');
      if (rec) rec.hidden = true;
    }

    // ==============================================================
    // Live total
    // ==============================================================

    updateTotal() {
      if (!this.totalEl || !this.totalValueEl) return;

      let total = 0;
      let itemCount = 0;

      this.cards.forEach((card) => {
        const skipped = card.querySelector('[data-bundle-card-skip]');
        if (skipped && skipped.checked) return;

        if (this.coverageEnabled && card.dataset.useCoverage === 'true' && card.__tinPick) {
          total += card.__tinPick.totalPrice;
          itemCount += card.__tinPick.picks.reduce((s, p) => s + p.qty, 0);
        } else {
          // Non-coverage card: just the selected variant's price × 1
          const input = card.querySelector('[data-bundle-variant-id]');
          const id = input ? parseInt(input.value, 10) : NaN;
          const v = card.__variants.find((vv) => vv.id === id);
          if (v) {
            total += v.price;
            itemCount += 1;
          }
        }
      });

      if (itemCount === 0) {
        this.totalEl.hidden = true;
      } else {
        this.totalValueEl.textContent = formatMoney(total);
        this.totalEl.hidden = false;
      }
    }

    updateSubmitButton() {
      if (!this.submitButtonText) return;

      let count = 0;
      let total = 0;
      this.cards.forEach((card) => {
        const skipped = card.querySelector('[data-bundle-card-skip]');
        if (skipped && skipped.checked) return;

        if (this.coverageEnabled && card.dataset.useCoverage === 'true' && card.__tinPick) {
          count += card.__tinPick.picks.reduce((s, p) => s + p.qty, 0);
          total += card.__tinPick.totalPrice;
        } else {
          count += 1;
          const input = card.querySelector('[data-bundle-variant-id]');
          const id = input ? parseInt(input.value, 10) : NaN;
          const v = card.__variants.find((vv) => vv.id === id);
          if (v) total += v.price;
        }
      });

      if (count === 0) {
        this.submitButtonText.textContent = 'Add to basket';
      } else if (this.coverageEnabled && this.areaInput && parseFloat(this.areaInput.value) > 0) {
        this.submitButtonText.textContent =
          'Add system — ' + formatMoney(total);
      } else {
        this.submitButtonText.textContent = 'Add to basket';
      }
    }

    // ==============================================================
    // Add to cart
    // ==============================================================

    collectItems() {
      const items = [];
      this.cards.forEach((card) => {
        const skipped = card.querySelector('[data-bundle-card-skip]');
        if (skipped && skipped.checked) return;

        if (this.coverageEnabled && card.dataset.useCoverage === 'true' && card.__tinPick) {
          card.__tinPick.picks.forEach((p) => {
            items.push({ id: p.variant.id, quantity: p.qty });
          });
        } else {
          const input = card.querySelector('[data-bundle-variant-id]');
          if (input && input.value) {
            items.push({ id: parseInt(input.value, 10), quantity: 1 });
          }
        }
      });
      return items;
    }

    getDrawerSections() {
      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer || typeof cartDrawer.getSectionsToRender !== 'function') return null;
      try {
        const sections = cartDrawer.getSectionsToRender();
        if (!Array.isArray(sections)) return null;
        return sections.map((s) => s.section).filter(Boolean).join(',');
      } catch (e) {
        return null;
      }
    }

    async submit(event) {
      event.preventDefault();
      if (this.isSubmitting) return;

      const items = this.collectItems();
      if (items.length === 0) {
        this.showError('Please select at least one product.');
        return;
      }

      // If coverage is enabled but no area entered, warn the user
      if (this.coverageEnabled && this.areaInput && !this.areaInput.value) {
        const hasCoverageCards = this.cards.some((c) => c.dataset.useCoverage === 'true');
        if (hasCoverageCards) {
          this.showError('Enter your project area to calculate the right quantities.');
          this.areaInput.focus();
          return;
        }
      }

      this.setLoading(true);
      this.hideError();

      const body = { items };
      const sections = this.getDrawerSections();
      if (sections) {
        body.sections = sections;
        body.sections_url = window.location.pathname;
      }

      try {
        const response = await fetch(`${window.Shopify.routes.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
          },
          body: JSON.stringify(body),
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.description || data.message || 'Could not add items to cart.');
        }

        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          if (data.sections && typeof cartDrawer.renderContents === 'function') {
            await cartDrawer.renderContents(data);
          } else if (typeof cartDrawer.refresh === 'function') {
            await cartDrawer.refresh();
            if (typeof cartDrawer.open === 'function') cartDrawer.open();
          }
          this.setLoading(false);
          return;
        }

        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        window.location.href = `${window.Shopify.routes.root || '/'}cart`;
      } catch (error) {
        this.setLoading(false);
        this.showError(error.message || 'Something went wrong. Please try again.');
      }
    }

    setLoading(isLoading) {
      this.isSubmitting = isLoading;
      if (!this.submitButton) return;
      this.submitButton.toggleAttribute('aria-busy', isLoading);
      this.submitButton.disabled = isLoading;
      this.submitButton.classList.toggle('is-loading', isLoading);
    }

    showError(message) {
      if (!this.errorMessage) return;
      this.errorMessage.textContent = message;
      this.errorMessage.hidden = false;
    }

    hideError() {
      if (!this.errorMessage) return;
      this.errorMessage.textContent = '';
      this.errorMessage.hidden = true;
    }
  }

  customElements.define('compact-product-bundle', CompactProductBundle);
})();


/* Scroll-hint animation unchanged from original */
(function scrollHint() {
  if (!('IntersectionObserver' in window)) return;
  if (window.matchMedia('(min-width: 640px)').matches) return;

  const grids = document.querySelectorAll('.compact-product-bundle .product-grid.swipe-on-mobile');
  grids.forEach((grid) => {
    let played = false;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (played || !entry.isIntersecting) return;
        if (entry.intersectionRatio < 0.4) return;
        played = true;
        observer.unobserve(grid);

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        setTimeout(() => {
          const firstCard = grid.querySelector('.grid');
          if (!firstCard) return;
          const gap = parseInt(getComputedStyle(grid).columnGap) || 16;
          const scrollTo = firstCard.offsetWidth + gap;
          grid.scrollTo({ left: scrollTo, behavior: 'smooth' });
          setTimeout(() => {
            grid.scrollTo({ left: 0, behavior: 'smooth' });
          }, 900);
        }, 400);
      });
    }, { threshold: [0, 0.4, 0.6] });
    observer.observe(grid);
  });
})();